import React, { useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { classNames } from 'primereact/utils';
import axiosInstance from '../../../config/axios.config';
import { useReportService, useInvalidateReportService } from '../../../hooks/queries/useReportService';
import ElectricityBillOutputComponent from '../output/ElectricityBillOutputComponent';
import ConsentCheckbox from './ConsentCheckbox';
import ReportBanner from '../ReportBanner';

const ElectricityBillInputComponent = () => {
    const { serviceId } = useParams();
    const toast = useRef(null);
    const submittingRef = useRef(false);

    const [formData, setFormData] = useState({ operatorCode: '', idNumber: '', consent: false });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState(null);
    // #161: one shared, cached query instead of a per-component fetch on every
    // mount. Concurrent consumers are deduped into a single request and a
    // revisit is served from cache; the balance is invalidated after a report
    // runs (below), which is what keeps a cached credit figure correct.
    const {
        data: fetchedServiceDetails,
        isFetching: fetchingService,
        isError: serviceError,
    } = useReportService(serviceId);
    const invalidateReportService = useInvalidateReportService();
    const serviceDetails = serviceError
        ? { available_credits: 'N/A', credits_required: 'N/A' }
        : (fetchedServiceDetails || null);

    const handleOperatorChange = (e) => {
        const value = (e.target.value || '').toUpperCase().trim();
        setFormData((prev) => ({ ...prev, operatorCode: value }));
        if (errors.operatorCode) setErrors((prev) => ({ ...prev, operatorCode: '' }));
    };

    const handleIdNumberChange = (e) => {
        const value = (e.target.value || '').trim();
        setFormData((prev) => ({ ...prev, idNumber: value }));
        if (errors.idNumber) setErrors((prev) => ({ ...prev, idNumber: '' }));
    };

    const handleConsentChange = (e) => {
        setFormData((prev) => ({ ...prev, consent: e.checked }));
        if (errors.consent) setErrors((prev) => ({ ...prev, consent: '' }));
    };

    const validate = () => {
        const next = {};
        if (!formData.operatorCode) next.operatorCode = 'Operator code is required';
        if (!formData.idNumber) next.idNumber = 'Consumer ID is required';
        if (!formData.consent) next.consent = 'Please confirm consent before submitting';
        return next;
    };

    const submit = async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.post('/surepass/utility/electricity/bill', {
                operator_code: formData.operatorCode,
                id_number: formData.idNumber,
                api_provider_service_id: parseInt(serviceId, 10),
            });
            if (!res.data.success) throw new Error(res.data.message || 'Lookup failed');
            setReportData(res.data);
            toast.current?.show({ severity: 'success', summary: 'Bill fetched', detail: 'Report ready below', life: 2000 });
            // Credits just changed. Invalidating updates every mounted consumer at
            // once and is ordered strictly after the deduction.
            invalidateReportService(serviceId);
        } catch (error) {
            console.error('Electricity bill fetch failed:', error?.message || 'Unknown error');
            const status = error.response?.status;
            const map = {
                500: 'Internal server error. Please try again later.',
                422: 'Please check the inputs and try again.',
                429: 'Too many requests. Please wait a minute before trying again.',
                400: 'Invalid input. Please verify and retry.',
                404: 'No bill found for this consumer ID.',
            };
            let detail = map[status] || error.response?.data?.message || error.message || 'Lookup failed';
            if (status === 402) {
                const c = error.response?.data || {};
                detail = `Insufficient credits. Required: ${c.required_credits ?? 'N/A'}, Available: ${c.current_balance ?? 'N/A'}`;
            }
            toast.current?.show({ severity: 'error', summary: 'Lookup failed', detail, life: 3500 });
        } finally {
            setLoading(false);
            submittingRef.current = false;
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (submittingRef.current) return;
        const next = validate();
        if (Object.keys(next).length > 0) {
            setErrors(next);
            toast.current?.show({ severity: 'warn', summary: 'Validation error', detail: 'Please fix the highlighted fields', life: 3000 });
            return;
        }
        setErrors({});
        submittingRef.current = true;
        const required = serviceDetails?.credits_required;
        const available = serviceDetails?.available_credits;
        if (typeof available === 'number' && typeof required === 'number' && available < required) {
            submittingRef.current = false;
            toast.current?.show({ severity: 'error', summary: 'Insufficient credits', detail: `Required: ${required}, Available: ${available}`, life: 3500 });
            return;
        }
        confirmDialog({
            message: (
                <div className="text-sm">
                    <div className="mb-2"><span className="text-600">Operator:</span> <span className="text-900 font-medium">{formData.operatorCode}</span></div>
                    <div className="mb-2"><span className="text-600">Consumer ID:</span> <span className="text-900 font-medium">{formData.idNumber}</span></div>
                    <div className="mb-2"><span className="text-600">Available credits:</span> <span className="text-900 font-medium">{available ?? 'N/A'}</span></div>
                    <div><span className="text-600">Credits to deduct:</span> <span className="text-900 font-medium">{required ?? 'N/A'}</span></div>
                </div>
            ),
            header: 'Confirm electricity bill lookup',
            icon: 'pi pi-question-circle text-primary pi-2x',
            acceptLabel: 'Yes, fetch',
            rejectLabel: 'Cancel',
            accept: submit,
            reject: () => { submittingRef.current = false; },
            style: { width: '90vw', maxWidth: '450px' },
            draggable: false,
        });
    };

    const handleAnother = () => {
        setReportData(null);
        setFormData({ operatorCode: '', idNumber: '', consent: false });
        setErrors({});
    };

    return (
        <>
            <Toast ref={toast} />
            <ConfirmDialog />
            <div className="grid">
                <div className="col-12">
                    <ReportBanner
                        icon="pi-bolt"
                        title="Electricity Bill"
                        subtitle="Fetch electricity bill details by operator code and consumer ID via Surepass"
                        availableCredits={serviceDetails?.available_credits}
                        requiredCredits={serviceDetails?.credits_required}
                        loading={fetchingService}
                    />
                    {!reportData ? (
                        <Card>
                            <form onSubmit={handleSubmit} noValidate>
                                <div className="grid">
                                    <div className="field col-12 md:col-6 lg:col-4">
                                        <label htmlFor="op" className="block font-medium text-sm mb-2">
                                            Operator Code <span className="text-red-500">*</span>
                                        </label>
                                        <IconField iconPosition="left" className="w-full">
                                            <InputIcon className="pi pi-bolt" />
                                            <InputText
                                                id="op"
                                                value={formData.operatorCode}
                                                onChange={handleOperatorChange}
                                                placeholder="e.g. APSPDCL"
                                                autoCapitalize="characters"
                                                spellCheck={false}
                                                disabled={loading}
                                                className={classNames('w-full p-inputtext-sm font-mono', { 'p-invalid': !!errors.operatorCode })}
                                                aria-describedby="op-help"
                                                aria-invalid={!!errors.operatorCode}
                                            />
                                        </IconField>
                                        {errors.operatorCode && (
                                            <small id="op-help" className="p-error block mt-1" role="alert">
                                                <i className="pi pi-exclamation-circle mr-1" aria-hidden="true" />
                                                {errors.operatorCode}
                                            </small>
                                        )}
                                    </div>
                                    <div className="field col-12 md:col-6 lg:col-5">
                                        <label htmlFor="cid" className="block font-medium text-sm mb-2">
                                            Consumer ID <span className="text-red-500">*</span>
                                        </label>
                                        <IconField iconPosition="left" className="w-full">
                                            <InputIcon className="pi pi-id-card" />
                                            <InputText
                                                id="cid"
                                                value={formData.idNumber}
                                                onChange={handleIdNumberChange}
                                                placeholder="e.g. 123456789012"
                                                inputMode="numeric"
                                                disabled={loading}
                                                className={classNames('w-full p-inputtext-sm font-mono', { 'p-invalid': !!errors.idNumber })}
                                                aria-describedby="cid-help"
                                                aria-invalid={!!errors.idNumber}
                                            />
                                        </IconField>
                                        {errors.idNumber && (
                                            <small id="cid-help" className="p-error block mt-1" role="alert">
                                                <i className="pi pi-exclamation-circle mr-1" aria-hidden="true" />
                                                {errors.idNumber}
                                            </small>
                                        )}
                                    </div>
                                </div>
                                <ConsentCheckbox
                                    checked={formData.consent}
                                    onChange={handleConsentChange}
                                    error={errors.consent}
                                    reportType="electricity-bill"
                                />
                                <div className="flex justify-content-end mt-3">
                                    <Button
                                        type="submit"
                                        label="Fetch Bill"
                                        icon="pi pi-arrow-right"
                                        iconPos="right"
                                        loading={loading}
                                        disabled={loading}
                                        size="small"
                                    />
                                </div>
                            </form>
                        </Card>
                    ) : (
                        <>
                            <div className="flex justify-content-end mb-3">
                                <Button
                                    label="Fetch another"
                                    icon="pi pi-refresh"
                                    severity="secondary"
                                    outlined
                                    size="small"
                                    onClick={handleAnother}
                                />
                            </div>
                            <ElectricityBillOutputComponent reportData={reportData} />
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default ElectricityBillInputComponent;
